import { ComponentTransport } from './component.transport';
import { Type } from 'main.core';
import { BaseEvent, EventEmitter } from 'main.core.events';
import { Store } from './store';

class Component
{
	static $store = new Store();

	constructor(data = {}, options = {})
	{
		this.__log('init');

		this.$private = {
			node: null,
			nodeInternal: false,
			state: '',
			data: {},
			components: {},
		};

		this.$options = {
			autoUpdate: Boolean(options.autoUpdate),
		};

		this.$events = {};
		this.$data = {};
		this.$components = {};
		this.stateToAnalyticsEventMap = options.stateToAnalyticsEventMap || {};

		this._processData(data || {});
		this._processComponents(this.components());

		this.$localization = this.localization();

		this._update = BX.debounce(this._update, 10, this);
	}

	render()
	{
		this.__log('render() method should be overridden');

		return this.constructor.name;
	}

	bindComponents(node)
	{
		if (BX.type.isElementNode(node))
		{
			const nodeList = node.querySelectorAll('[data-component]');
			[].forEach.apply(nodeList, [(placementNode) => {
				const componentName = placementNode.getAttribute('data-component');
				if (typeof this.$components[componentName] !== 'undefined')
				{
					this.$components[componentName].appendTo(placementNode);
				}
			}]);
		}
		else
		{
			this.__log('Unable to bind components inside this', node);
		}
	}

	on(event, handler)
	{
		if (typeof this.$events[event] === 'undefined')
		{
			this.$events[event] = [];
		}

		if (
			handler instanceof Function
			&& !this.$events[event].includes(handler)
		)
		{
			this.$events[event].push(handler);
		}

		return this;
	}

	off(event, handler)
	{
		if (
			typeof this.$events[event] !== 'undefined'
			&& handler instanceof Function
		)
		{
			this.$events[event] = this.$events[event].filter((cb) => cb !== handler);
		}

		return this;
	}

	callEvent(event, parameters = [])
	{
		if (typeof this.$events[event] !== 'undefined')
		{
			this.$events[event].forEach((cb) => cb.apply(this, parameters));
		}
	}

	description()
	{
		return {};
	}

	components()
	{
		return {};
	}

	localization()
	{
		return {};
	}

	appendTo(el)
	{
		if (!el || !BX.type.isElementNode(el))
		{
			this.__log('Wrong node to append component to', el);
			this.throwError('Unable to append component to this');
		}

		if (el !== this.node())
		{
			BX.cleanNode(el);

			while (this.node().firstChild)
			{
				el.appendChild(this.node().firstChild);
			}

			if (this.$private.nodeInternal)
			{
				BX.remove(this.node());
			}

			this.$private.node = el;
			this.$private.nodeInternal = false;

			this.update();
			this.tryEmitCurrentStateEvent();
		}

		return this;
	}

	node()
	{
		if (this.$private.node === null)
		{
			this.$private.node = BX.create('DIV');
			this.$private.nodeInternal = true;
		}

		return this.$private.node;
	}

	setLocalization(localization, value = null)
	{
		if (typeof localization === 'string')
		{
			this.$localization[localization] = value;
		}
		else
		{
			this.$localization = {
				...this.$localization,
				...localization,
			};
		}

		return this;
	}

	update(forceUpdate = false)
	{
		this._update(forceUpdate);

		return this;
	}

	_update(forceUpdate = false)
	{
		this.__log('update');
		const state = JSON.stringify(this.$private.data);

		if (forceUpdate || state !== this.$private.state)
		{
			this.onBeforeUpdate();

			this.__log('render');

			const renderResult = this.render();

			if (BX.type.isString(renderResult))
			{
				BX.adjust(this.node(), {
					html: renderResult,
				});
			}
			else if (BX.type.isDomNode(renderResult))
			{
				BX.cleanNode(this.node());
				BX.adjust(this.node(), {
					children: [renderResult],
				});
			}
			else if (BX.type.isArray(renderResult))
			{
				BX.cleanNode(this.node());
				BX.adjust(this.node(), {
					children: renderResult.map((element) => {
						return (element instanceof Component) ? element.update().node() : element;
					}),
				});
			}
			else if (renderResult instanceof Component)
			{
				BX.cleanNode(this.node());
				BX.adjust(this.node(), {
					children: [
						renderResult.update().node(),
					],
				});
				renderResult.tryEmitCurrentStateEvent();
			}
			else if (BX.type.isFunction(renderResult))
			{
				renderResult.apply(this, [this.node()]);
			}
			else
			{
				this.throwError('render() method returned unexpected result');
			}

			this.$private.state = JSON.stringify(this.$private.data);

			this.onAfterUpdate();
		}
	}

	throwError(text)
	{
		throw `${this.constructor.name}: ${text}`;
	}

	onBeforeProcessData(inputData)
	{}

	onProcessData(inputData)
	{}

	onAfterProcessData()
	{}

	onBeforeUpdate()
	{}

	onAfterUpdate()
	{}

	get(property)
	{
		return this.$data[property];
	}

	set(property, value)
	{
		const oldValue = this.$data[property];
		const onChangeCallbackName = `onChange${property.replaceAll(/^(.)/g, (letter) => letter.toUpperCase())}`;
		const needRunOnChangeCallback =			(oldValue !== value)
			&& Type.isFunction(this[onChangeCallbackName])
		;
		this.$data[property] = value;
		if (needRunOnChangeCallback)
		{
			this[onChangeCallbackName](value, oldValue);
		}

		return this;
	}

	_processComponents()
	{
		const components = this.components();
		const def = (name) => {
			return {
				get: () => {
					if (typeof this.$private.components[name] === 'undefined')
					{
						this.$private.components[name] = components[name].apply(this);
					}

					return this.$private.components[name];
				},
				configurable: true,
			};
		};

		for (const componentName in components)
		{
			if (components.hasOwnProperty(componentName))
			{
				if (components[componentName] instanceof Function)
				{
					Object.defineProperty(this.$components, componentName, def(componentName));
				}
				else if (components[componentName] instanceof Component)
				{
					this.$components[componentName] = components[componentName];
				}
			}
		}
	}

	_processData(inputData)
	{
		let data = inputData || {};

		if (BX.type.isFunction(data))
		{
			data = data.apply(this);
		}

		this.onBeforeProcessData(inputData);

		let description = this.description();

		if (BX.type.isFunction(description))
		{
			description = description.apply(this);
		}

		Object.freeze(description);

		this._validateRequired(data, description);

		if (BX.type.isPlainObject(data))
		{
			for (const property in data)
			{
				if (data.hasOwnProperty(property) && this._validateDescription(property, data[property], description))
				{
					this.$private.data[property] = data[property];
				}
			}

			this.onProcessData(this.$private.data);

			const def = (property) => {
				return {
					get: () => {
						return this.$private.data[property];
					},
					set: (value) => {
						if (value !== this.$private.data[property])
						{
							if (this._validateDescription(property, value, description))
							{
								this.$private.data[property] = value;
							}

							if (this.$options.autoUpdate)
							{
								this.update();
							}
						}
					},
				};
			};

			const cap = (s) =>
			{ return s.toUpperCase();
			};

			const get = (i) =>
			{ return () =>
			{ return this.get(i);
			};
			};

			const set = (i) =>
			{ return (v) =>
			{ return this.set(i, v);
			};
			};

			for (const property in description)
			{
				if (description.hasOwnProperty(property))
				{
					Object.defineProperty(this.$data, property, def(property));
					const propertyNameCamel = property.replaceAll(/^(.)/g, cap);
					if (typeof this[`get${propertyNameCamel}`] === 'undefined')
					{
						this[`get${propertyNameCamel}`] = get(property);
					}

					if (typeof this[`set${propertyNameCamel}`] === 'undefined')
					{
						this[`set${propertyNameCamel}`] = set(property);
					}
				}
			}
		}

		this.onAfterProcessData();
	}

	_validateRequired(data, description)
	{
		for (const property in description)
		{
			if (description.hasOwnProperty(property))
			{
				if (typeof data[property] === 'undefined')
				{
					if (description[property].required)
					{
						this.throwError(`Property ${property} is required`);
					}
					else if (typeof description[property].defaultValue !== 'undefined')
					{
						data[property] = description[property].defaultValue;
					}
				}

				if (typeof description[property].fields !== 'undefined')
				{
					this._validateRequired(data[property], description[property].fields);
				}
			}
		}
	}

	_validateDescription(property, value, description)
	{
		if (typeof description[property] !== 'undefined' && BX.type.isPlainObject(description[property]))
		{
			if (typeof description[property].type !== 'undefined')
			{
				const typeList = this._validateTypeList(
					BX.type.isArray(description[property].type)
						? description[property].type
						: [description[property].type],
				);

				let typeFound = false;
				for (const type of typeList)
				{
					if (
						typeof type === 'function' && value instanceof type
						|| typeof value === type
					)
					{
						typeFound = true;
						break;
					}
				}

				if (!typeFound)
				{
					this.throwError(`Property ${property} must be of ${description[property].type.name} type`);
				}
			}

			if (typeof description[property].required !== 'undefined' && description[property].required && value === null)
			{
				this.throwError(`Property${property} is required, null given`);
			}

			if (typeof description[property].fields !== 'undefined')
			{
				for (const field in description[property].fields)
				{
					if (description[property].fields.hasOwnProperty(field) && typeof value[field] !== 'undefined' && !this._validateDescription(field, value[field], description[property].fields))
					{
						return false;
					}
				}
			}

			return true;
		}

		return false;
	}

	_validateTypeList(typeList)
	{
		const result = [];

		for (const type of typeList)
		{
			result.push(type);
		}

		if (typeList.includes(String))
		{
			result.push('string');
		}
		else if (typeList.includes('string'))
		{
			result.push(String);
		}

		if (typeList.includes(Number))
		{
			result.push('number');
		}
		else if (typeList.includes('number'))
		{
			result.push(Number);
		}

		if (typeList.includes(Boolean))
		{
			result.push('boolean');
		}
		else if (typeList.includes('boolean'))
		{
			result.push(Boolean);
		}

		return BX.util.array_unique(result);
	}

	__log(message)
	{
		if (Component.DEBUG_MODE)
		{
			console.log(this.constructor.name, message);
		}
	}

	updateCurrentState(newState)
	{
		const currentState = this.$data.currentState;
		if (currentState !== newState)
		{
			this.$data.previousState = currentState;
			this.$data.currentState = newState;
			this.emitChangeStateEvent(newState);
		}
	}

	rollbackCurrentState()
	{
		const currentState = this.$data.currentState;
		const previousState = this.$data.previousState;
		this.$data.currentState = previousState;
		this.$data.previousState = currentState;
		if (currentState !== previousState)
		{
			this.emitChangeStateEvent(previousState);
		}
	}

	onChangeCurrentState(newState, oldState)
	{
		this.emitChangeStateEvent(newState);
	}

	tryEmitCurrentStateEvent()
	{
		if (this.$private.data.hasOwnProperty('currentState'))
		{
			// emit initial state event on first component render
			this.emitChangeStateEvent(this.$private.data.currentState);
		}
	}

	emitChangeStateEvent(newState)
	{
		const eventName =			this.stateToAnalyticsEventMap.hasOwnProperty(newState)
			? this.stateToAnalyticsEventMap[newState]
			: null
		;

		if (eventName)
		{
			this.emitAnalyticsEvent(eventName);
		}

		if (String(newState).length > 0)
		{
			const event = new BaseEvent({ data: newState });
			EventEmitter.emit(this, 'onChangeState', event);
		}
	}

	emitAnalyticsEvent(eventName: string, eventArguments: Object = null): Promise
	{
		if (BX.Network.hasOwnProperty('Analytics'))
		{
			const analytics = BX.Network.Analytics.getInstance();
			if (analytics)
			{
				return analytics.registerEvent(eventName, eventArguments);
			}
		}

		return Promise.resolve();
	}
}

Component.DEBUG_MODE = false;

export {
	Component,
	ComponentTransport,
};
